/**
 * document.js
 * @author: Bernhard Wolkerstorfer
 * 
 * Description: Send To Kindle Document
 * 
 * Copyright (c) 2011 Amazon.com, Inc. All rights reserved.
 */
import { sanitize } from "dompurify";
import {STK_DATA_TYPE, STK_FILE_TYPE, STK_STRING_UNKNOWN} from "../../src-common/constants";

/**
 * @constructor
 * @param {STK_DATA_TYPE} dataType
 * @param {STK_FILE_TYPE} fileType
 * @param {jQuery=} $c
 */
const SendToKindleDocument = function (dataType, $c, fileType) {
    // ######## Privileged Members ########
    this.token           = undefined;
    this.title           = undefined;
    this.author          = undefined;
    this.publicationDate = undefined;
    this.source          = undefined;
    this.url             = undefined;
    this.metadata        = undefined;
    this.contentNode     = undefined;
    this.binaryData      = undefined;
    /**
     * Before calling WebExtractionService, an unique id will be created to identify and connect the extraction action with whole workflow.
     * This id will be generated when calling WebExtractionService, and be used in `Send-v2` API
     * @type {string}
     */
    this.extractionID    = undefined;
    /**
     * Content of epub file
     * @type {string}
     */
    this.content      = undefined;
    /**
     * The file type of the extracted content
     * @type {STK_FILE_TYPE}
     */
    this.fileType        = fileType || STK_FILE_TYPE.HTML;
    /**
     * The data type of the extracted content
     * @type {STK_DATA_TYPE}
     */
    this.dataType        = dataType;
    /**
     * Base64 string of epub file
     * @type {string}
     */
    this.epubString      = undefined;

    // --------- Content ---------
    if (this.dataType === "text/html" && $c) {
        this.contentNode = $c;
    }
    else {
        this.binaryData = $c;
    }
    
    // -------- Conversion Format --------
    this.$kindleContent = $(
        "<div>" +
        "   <div class='s2k-article'>" +
        "       <div class='s2k-article-header'>" +
        "           <div class='s2k-title'></div>" +
        "           <hr style='background: transparent; color: transparent; border-left: none; border-right: none; border-top: none; border-bottom: 1px dashed #000;' />" +
        "           <div class='s2k-byline'>" +
        "               <span class='s2k-author'></span>" +
        "               <a class='s2k-source-short'></a>" +
        "               <span class='s2k-timestamp'></span>" +
        "           </div>" +
        "       </div>" +
        "       <div class='s2k-article-body'></div>" +
        "       <div class='s2k-article-footer'>" +
        "            <hr style='background: transparent; color: transparent; border-left: none; border-right: none; border-top: none; border-bottom: 1px dashed #000;' />" +
        "            Source: <a class='s2k-source-long'></a>" +
        "       </div>" +
        "   <div>" +
        "</div>");
};

SendToKindleDocument.prototype = {
    // ######## Constants ########
    BYLINE_SEPARATOR: "<span class='byline-separator'>&nbsp;&#149;&nbsp;</span>",
    
    // ######## Methods ########
    /**
     * Set extraction result.
     * @param $c    Content
     */
    setContent: function ($c) {
        // --------- Content ---------
        if (this.dataType === "text/html") {
            this.contentNode = $c.clone(true);
        }
        else {
            this.binaryData = $c;
        }
    },
    
    /**
     * Escape HTML for plain insertion.
     * @param {string} textToEscape Text that should be escaped.
     * @returns {string} Escaped Text
     */
    escapeHTML: function (textToEscape) {
        return textToEscape.replace(/[&"<>]/g, function (ch) {
            return { "&": "&amp;", '"': "&quot", "<": "&lt;", ">": "&gt;" }[ch];
        });
    },
    
    /**
     * Format a content template.
     * @param {jQuery} $ct   Content Template
     * @param {Object} pdoc  Kindle Personal Document
     */
    formatContentTemplate: function ($ct, pdoc) {
        if (this.contentNode) {
            // Set the article content.
            var entry = $ct.find(".s2k-article-body");
            entry.empty().append(this.contentNode.clone(true));
            
            $.each(entry.find(".s2k-default-image"), function () {
                $(this).wrap("<div class='s2k-image-wrap'></div>");
            });
            
            $.each(entry.find("blockquote"), function () {
                var content = $(this).contents();
                var $n = $("<div class='s2k-blockquote'></div>").append(content);
                $(this).replaceWith($n);
            });
            
            entry.find(":header").addClass(".s2k-text-header");
            
            // Set metadata.
            $ct.find(".s2k-title").text(pdoc.title);
            
            // Set author
            if (pdoc.author) {
                $ct.find(".s2k-author")
                    .append("By ")
                    .append($("<span class='fn'></span>").text(pdoc.author))
                    .append(this.BYLINE_SEPARATOR);
            }
    
            // Set publication date.
            if (pdoc.publicationDate) {
                $ct.find(".s2k-timestamp")
                    .append(this.BYLINE_SEPARATOR)
                    .append(pdoc.publicationDate);
            }
                                
            // Set source short link.
            $ct.find(".s2k-source-short").attr("href", pdoc.url);
            $ct.find(".s2k-source-short").text(pdoc.source);

            // Generate the footer URL text.
            var footerUrl = pdoc.url;
            if (footerUrl.length > 140) {
                footerUrl = footerUrl.substring(0, 137) + "...";
            }
            
            // Set the footer URL text.
            $ct.find(".s2k-source-long").attr("href", pdoc.url);
            $ct.find(".s2k-source-long").text(footerUrl);
            
            $ct.each(function () {
                sanitize(this, { IN_PLACE: true });
            });

            return $ct;
        }
    },
    
    /**
     * Fetch Kindle formatted content.
     * 
     * @return {string} Kindle-formatted Content
     */
    getKindleContent: function () {
        // Format the content template
        this.formatContentTemplate(this.$kindleContent, this, true);
        
        //@preserve Create HTML document for KindleGen, this string will not be displayed parsed into a DOM.
        return "<ht" + "ml>" +
                     "<he" + "ad>" +
                     "  <meta http-equiv='Content-Type' content='text/html;charset=utf-8' />" +
                     "  <title>" + this.escapeHTML(this.title) + "</title>" +
                     "  <style type='text/css'>" +
                     "    .s2k-article-footer { font-size: 0.8em; text-align: left }" +
                     "    .s2k-title { font-size: 1.5em; font-weight: bold; color: black; }" +
                     "    .s2k-byline { font-size: 0.8em; font-style: italic; color: black; margin-bottom: 1em; }" +  
                     "    .s2k-author .fn { font-weight: bold }" +
                     "    .s2k-image-wrap { width: 100%; text-align: center; }" +
                     "    .s2k-default-image { border: #000 solid 1px; line-height: 1em; margin: 0px auto; padding: 0.25em; text-align: center; font-size: 50%; }" +
                     "    .s2k-blockquote { margin: 0em 2em 0em 2em; font-style: italic; }" +
                     "    .s2k-text-header { margin: 1em }" +
                     "    .s2k-table table { width: 100%; }" +
                     "    .s2k-table table, .s2k-table tbody, .s2k-table tr, .s2k-table td, .s2k-table th { margin: 20px 0px; border: none; border-collapse: collapse; border-spacing: 2px; font-size: inherit; }" +
                     "    .s2k-table td, .s2k-table th { border-top: none; border-left: none; border-right: none; border-bottom: 1px solid #111; }" +
                     "    .s2k-maps-table { width: 100%; border-collapse: collapse; font-size: inherit; }" +
                     "    .s2k-maps-table td { vertical-align: middle; padding-bottom: 5px; padding-top: 10px; } " +
                     "    .s2k-waypoint-header .s2k-maps-text { font-weight: bold; }" +
                     "    .s2k-maps-waypoints { margin-top: 20px; }" +
                     "    .s2k-waypoint-header { border-top: 1px solid #000; border-bottom: 1px solid #000; }" +
                     "    .s2k-waypoint-info { font-size: 70%; text-align: right;  border-top: 1px solid #000;}" +
                     "    .s2k-maps-distance { width: 10%; font-size: 80%; text-align: right; }" +
                     "    .s2k-maps-num { width: 5%; font-size: 80%; text-align: center; padding: 0px 10px; }" +
                     "    .s2k-maps-icon { width: 5%; padding: 0px 10px; text-align: center; }" +
                     "    .s2k-maps-text { padding-left: 20px; }" +
                     "    .s2k-maps-step-duration { font-size: 80%; }" +
                     "    .s2k-maps-step-notice { color: #800000; font-size: 80%; }" +
                     "    .s2k-maps-copyright, .s2k-maps-disclosure { margin-top: 20px; line-height: 1.5em; text-align: center; font-size: 70%; }" +
                     "    .s2k-maps-disclosure { margin-top: 50px; }" +
                     "    tr.s2k-darkrow { background-color: #e8e7e3; }" +
                     "    p { margin-bottom: 1em; }" +
                     "  </style>" +
                     "</head>" +
                     "<bo" + "dy>" +
                     "  " + this.$kindleContent.html() +
                     " </bo" + "dy>" +
                     "</ht" + "ml>";
    },

    /**
     * Load content from storage JSON.
     * 
     * @param {Object} data
     */
    fromStorageJson: function (data) {
        this.token = data.token || undefined;
        this.title = data.title || data.domain || STK_STRING_UNKNOWN;
        this.author = data.author || undefined;
        this.url = data.url || undefined;
        this.source = data.source || undefined;
        this.publicationDate = data.publicationDate || undefined;
        this.contentNode = data.content ? $(data.content) : undefined;
        this.content = data.content;
        this.binaryData = data.binaryData || undefined;
        this.fileType = data.fileType || STK_FILE_TYPE.HTML;
        this.dataType = data.dataType || STK_DATA_TYPE.HTML;
        this.epubString = data.epubString || undefined;
        this.metadata = data.metadata || undefined;
        this.extractionID = data.extractionID || undefined;
    },
    
    /**
     * Create JSON-formatted document.
     *
     * @return {Object} JSON-formatted content.
     */
    asStorageJson: function () {
        // Load content in correct format.
        let formattedContent, content;
        // For new logic, the content is extracted from epub file
        if (this.content) {
            content = this.content;
        }

        // Here is the old logic, to deal with the content node
        if (this.contentNode) {
            // @preserve Generate and sanitize a HTML document for sending.
            formattedContent = this.getKindleContent();
            // @preserve Generate a HTML fragment for storage, and sanitize.
            content = sanitize($("<div />").append(this.contentNode).addBack().html());
        }
        
        // Return JSON transfer object.
        return {
            "token": this.token,
            "title": this.title,
            "author": this.author,
            "url": this.url,
            "source": this.source,
            "publicationDate": this.publicationDate,
            "content": content,
            "formattedContent": formattedContent,
            "binaryData": this.binaryData || undefined,
            "fileType": this.fileType,
            "dataType": this.dataType,
            "epubString": this.epubString,
            "metadata": this.metadata,
            "extractionID": this.extractionID
        };
    }
};

export { SendToKindleDocument };
